// ============ RESPOSTAS ============

/** Resposta individual de um avaliador para uma pergunta */
export interface IAnswer {
  evaluatorId: string;
  evaluatorName: string;
  evaluatorType: number; // 528210000 = Auto, 122470001 = Team Leader, etc
  qualification: string; // "1", "Yes", texto livre, etc
}

// ============ PERGUNTAS ============

/** Pergunta individual com todas as respostas dos avaliadores */
export interface IQuestion {
  questionId: string;
  questionText?: string; // Pode não vir em alguns casos
  questionType: string; // 2 = Rating, 4 = Yes/No, etc
  answers: IAnswer[];
}

// ============ CATEGORIAS ============

/** Categoria com suas perguntas agrupadas */
export interface ICategory {
  categoryId: string;
  categoryName: string; // "Competências Específicas e Comportamentais"
  order?: number;
  questions: IQuestion[];
}

// ============ AVALIADORES ============

/** Tipo enum para os avaliadores (baseado nos valores do backend) */
export enum EvaluatorType {
  AutoEvaluation = 528210000,
  TeamLeader = 122470001,
  Manager = 122470000,
  Colleague = 122470002,
  Friend = 122470003,
  Client = 122470004,
}

/** Mapeamento de tipo numérico para label amigável */
export const evaluatorTypeLabel: Record<number, string> = {
  [EvaluatorType.AutoEvaluation]: "Autoavaliação",
  [EvaluatorType.TeamLeader]: "Team Leader",
  [EvaluatorType.Manager]: "Manager",
  [EvaluatorType.Colleague]: "Colega trabalho",
  [EvaluatorType.Friend]: "Colega social",
  [EvaluatorType.Client]: "Cliente",
};

/** Avaliador individual com informações básicas */
export interface IEvaluator {
  id: string;
  evaluatorType: number; // 528210000, 122470001, etc
  evaluatorTypeLabel: string; // "Team Leader", "Autoavaliação", etc
  statusCode?: number;
  name: string; // "Eduardo Augusto", "Rafael Silva", "Microsoft"
  clientName?: string; // Apenas para clientes
}

// ============ AVALIAÇÃO ============

/** Informações da avaliação */
export interface IEvaluation {
  evaluationId: string;
  endDate: string; // ISO string
  year: number;
  hasReport?: boolean;
  status?: number;
  comment?: string;
  finalEvaluation?: string;
}

// ============ RELATÓRIO COMPLETO ============

/** Dados completos do relatório do backend (payload bruto) */
export interface IReportPayload {
  evaluation: IEvaluation[];
  evaluators: Array<{
    id: string;
    evaluatorTypeChoice: string; // "Colaborador", "Team Leader", "Client"
    statusCode?: number;
    evaluatorEmployeeName?: string;
    clientName?: string;
  }>;
  allAnswers: ICategory[];
  canHrComplete: boolean;
}

/** Dados processados para renderização no frontend */
export interface IReportData {
  evaluationId: string;
  evaluationYear: number;
  evaluationEndDate: string;
  evaluationStatus?: number;
  hasReport: boolean;
  canHrComplete: boolean;
  hrComment?: string;
  finalEvaluation?: string;

  // Avaliadores agrupados por tipo
  evaluatorsByType: {
    selfEvaluation?: IEvaluator;
    teamLeaders: IEvaluator[];
    managers: IEvaluator[];
    colleagues: IEvaluator[];
    friends: IEvaluator[];
    clients: IEvaluator[];
  };

  // Categorias com perguntas e respostas
  categories: ICategory[];

  // Resumo
  summary: {
    totalEvaluators: number;
    totalCategories: number;
    totalQuestions: number;
  };
}

// ============ PROPS DE COMPONENTES ============

/** Props do componente Insights */
export interface IInsightsProps {
  evaluators: {
    selfEvaluation?: IEvaluator;
    teamLeaders: IEvaluator[];
    managers: IEvaluator[];
    colleagues: IEvaluator[];
    friends: IEvaluator[];
    clients: IEvaluator[];
  };
  employeeName: string;
}
